import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, AlertTriangle, Scale, Ban } from 'lucide-react';

const TermsOfUsePage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-[#003D5C] py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <Link to="/" className="inline-flex items-center text-blue-200 hover:text-white mb-4 transition-colors">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Link>
          <h1 className="text-3xl md:text-4xl font-bold text-white">Terms of Use</h1>
          <p className="text-blue-200 mt-2">Last updated: December 2025</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        <div className="prose prose-lg max-w-none">
          
          <div className="bg-[#F0F7FF] p-6 rounded-xl mb-8 border-l-4 border-[#0077B6]">
            <p className="text-gray-700 m-0">
              Welcome to Utmost Financial Inc. By accessing or using our website and services, you agree to be bound by these Terms of Use. Please read them carefully.
            </p>
          </div>

          <section className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#0077B6] rounded-lg flex items-center justify-center">
                <FileText className="h-5 w-5 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 m-0">Acceptance of Terms</h2>
            </div>
            <p className="text-gray-600">
              By accessing this website, you acknowledge that you have read, understood, and agree to be bound by these Terms of Use and our Privacy Policy. If you do not agree to these terms, please do not use our website or services.
            </p>
          </section>

          <section className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#D4AF37] rounded-lg flex items-center justify-center">
                <Scale className="h-5 w-5 text-[#003D5C]" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 m-0">Services Description</h2>
            </div>
            <p className="text-gray-600">
              Utmost Financial Inc. provides mortgage brokerage, insurance, and investment services. Our services include:
            </p>
            <ul className="text-gray-600 space-y-2">
              <li>Mortgage brokerage services through Dominion Lending Centres Utmost Financial</li>
              <li>Life, critical illness, disability, and travel insurance products</li>
              <li>Investment solutions including RRSPs, TFSAs, RESPs, and segregated funds</li>
              <li>Financial planning and consultation services</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800">Use of Website</h2>
            <p className="text-gray-600">You agree to use this website only for lawful purposes and in accordance with these Terms. You agree NOT to:</p>
            <ul className="text-gray-600 space-y-2">
              <li>Use the website in any way that violates applicable laws or regulations</li>
              <li>Attempt to gain unauthorized access to any part of the website</li>
              <li>Use any automated system to access the website</li>
              <li>Transmit any viruses, malware, or harmful code</li>
              <li>Collect or harvest personal information from other users</li>
            </ul>
          </section>

          <section className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#0077B6] rounded-lg flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 m-0">Disclaimer</h2>
            </div>
            <p className="text-gray-600">
              The information on this website is for general informational purposes only and does not constitute financial, legal, or professional advice. While we strive to keep information accurate and up-to-date:
            </p>
            <ul className="text-gray-600 space-y-2">
              <li>Insurance quotes and mortgage rates shown are estimates only</li>
              <li>Actual rates and premiums depend on individual circumstances and underwriting</li>
              <li>Past performance of investments does not guarantee future results</li>
              <li>Always consult with a licensed professional before making financial decisions</li>
            </ul>
          </section>

          <section className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#D4AF37] rounded-lg flex items-center justify-center">
                <Ban className="h-5 w-5 text-[#003D5C]" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 m-0">Limitation of Liability</h2>
            </div>
            <p className="text-gray-600">
              To the fullest extent permitted by law, Utmost Financial Inc. shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from:
            </p>
            <ul className="text-gray-600 space-y-2">
              <li>Your use or inability to use the website</li>
              <li>Any errors or omissions in website content</li>
              <li>Unauthorized access to your personal information</li>
              <li>Any third-party conduct on the website</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800">Intellectual Property</h2>
            <p className="text-gray-600">
              All content on this website, including text, graphics, logos, images, and software, is the property of Utmost Financial Inc. or its licensors and is protected by Canadian and international copyright laws. You may not reproduce, distribute, or create derivative works without our written permission.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800">Governing Law</h2>
            <p className="text-gray-600">
              These Terms of Use shall be governed by and construed in accordance with the laws of the Province of Manitoba and the federal laws of Canada applicable therein. Any disputes shall be subject to the exclusive jurisdiction of the courts of Manitoba.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800">Changes to Terms</h2>
            <p className="text-gray-600">
              We reserve the right to modify these Terms of Use at any time. Changes will be effective immediately upon posting to the website. Your continued use of the website after changes constitutes acceptance of the modified terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800">Contact Us</h2>
            <p className="text-gray-600">
              If you have questions about these Terms of Use, please contact us:
            </p>
            <div className="bg-gray-50 p-4 rounded-lg mt-4">
              <p className="text-gray-700 m-0"><strong>Utmost Financial Inc.</strong></p>
              <p className="text-gray-600 m-0">A-970 Sargent Avenue, Winnipeg, MB R3E0E3</p>
              <p className="text-gray-600 m-0">Email: legal@utmostfinancial.ca</p>
              <p className="text-gray-600 m-0">Phone: 1-877-239-9060</p>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default TermsOfUsePage;
